import React, { useState, useEffect, useCallback } from "react";
import { Space, Typography, Descriptions, Divider, Modal, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ShopOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { fetchSalesOrders } from "../../../../api/inventory/salesOrderApi";
import type { SalesOrder, SalesOrderItem } from "../../../../types/inventory/OrderTypes";
import { useNotifications } from "../../../../context/NotificationContext";
import StatusTag from "../../../../components/shared/StatusTag";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";
import { DataTable } from "../../../../components/shared";

const { Text } = Typography;

const financialTag = (s?: string | null) =>
	s ? <StatusTag status={s} />
		: <Text type="secondary" >—</Text>;

const fulfillmentTag = (s?: string | null) => <StatusTag status={s ?? "unfulfilled"} />;

const itemCols: ColumnsType<SalesOrderItem> = [
	{ title: "Product", key: "product", render: (_: any, item) => <Text >{item.product?.name ?? `Product #${item.product_id}`}</Text> },
	{ title: "Variant", key: "variant", width: 140, render: (_: any, item) => <Text type="secondary" >{item.variant?.formatted_title ?? "—"}</Text> },
	{ title: "Qty", dataIndex: "quantity", key: "qty", width: 60, render: (v: number) => <Text >{v}</Text> },
	{ title: "Unit Price", dataIndex: "unit_price", key: "unit_price", width: 100, render: (v: number) => <Text >${Number(v).toFixed(2)}</Text> },
	{ title: "Total", dataIndex: "total_price", key: "total_price", width: 100, render: (v: number) => <Text >${Number(v).toFixed(2)}</Text> },
];

const OrderDetail: React.FC<{ order: SalesOrder }> = ({ order }) => (
	<div>
		<DataTable<SalesOrderItem> bare columns={itemCols} dataSource={order.items ?? []} rowKey={(r) => r.so_item_id ?? Math.random()} pagination={false} style={{ marginBottom: order.shopify_shipping_address ? 12 : 0 }} />
		{(order.shopify_shipping_address || order.shopify_billing_address) && (
			<>
				<Divider style={{ margin: "8px 0" }} />
				<Space size={32} wrap>
					{order.shopify_shipping_address && (
						<Descriptions size="small" title={<Text style={{ fontSize: 12, fontWeight: 600 }}>Shipping</Text>} column={1} style={{ minWidth: 180 }}>
							<Descriptions.Item label={null}>
								<Text >
									{[order.shopify_shipping_address.name, order.shopify_shipping_address.address1, order.shopify_shipping_address.address2, order.shopify_shipping_address.city, order.shopify_shipping_address.province, order.shopify_shipping_address.country, order.shopify_shipping_address.zip].filter(Boolean).join(", ")}
								</Text>
							</Descriptions.Item>
						</Descriptions>
					)}
					{(order.shopify_total_tax != null || order.shopify_total_shipping != null) && (
						<Space direction="vertical" size={2}>
							{order.shopify_total_shipping != null && <Text ><Text type="secondary">Shipping:</Text> ${Number(order.shopify_total_shipping).toFixed(2)}</Text>}
							{order.shopify_total_tax != null && <Text ><Text type="secondary">Tax:</Text> ${Number(order.shopify_total_tax).toFixed(2)}</Text>}
						</Space>
					)}
				</Space>
			</>
		)}
	</div>
);

interface OrdersTabProps {
	patient: Pick<Patient, "id">;
	emptyTitle?: string;
}

const OrdersTab: React.FC<OrdersTabProps> = ({
	patient,
	emptyTitle = "No orders found for this patient",
}) => {
	const { showError } = useNotifications();
	const [orders, setOrders] = useState<SalesOrder[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const perPage = loadUserPreferences().tableRowsPerPage || 10;
	const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null);

	const load = useCallback(async (p: number) => {
		if (!patient.id) return;
		setLoading(true);
		try {
			const res = await fetchSalesOrders({ assignee_type: "user", assignee_id: patient.id, page: p, per_page: perPage, sort_field: "sales_order_id", sort_direction: "desc" });
			setOrders(res.data);
			setTotal(res.total);
		} catch {
			showError("Failed to load orders");
		} finally {
			setLoading(false);
		}
	}, [patient.id]);

	useEffect(() => { load(page); }, [page, load]);

	const columns: ColumnsType<SalesOrder> = [
		{
			title: "Order", key: "order_ref",
			render: (_: any, r) => (
				<Space direction="vertical" size={0}>
					<Space size={4}>
						{r.is_shopify_order && <ShopOutlined style={{ color: "#96bf48" }} />}
						<Text strong >{r.shopify_order_identifier ?? `#${r.sales_order_id}`}</Text>
					</Space>
				</Space>
			),
		},
		{ title: "Date", dataIndex: "order_date", key: "order_date", render: (d: string) => <Text >{d ? format(new Date(d), "dd MMM yyyy") : "—"}</Text> },
		{ title: "Status", dataIndex: "status", key: "status", render: (s: string) => s ? <StatusTag status={s} /> : <Text type="secondary" >—</Text> },
		{ title: "Financial", dataIndex: "shopify_financial_status", key: "financial", render: financialTag },
		{ title: "Fulfillment", dataIndex: "shopify_fulfillment_status", key: "fulfillment", render: fulfillmentTag },
		{ title: "Items", key: "items", render: (_: any, r) => <Text >{r.items?.length ?? 0}</Text> },
		{ title: "Total", dataIndex: "total_amount", key: "total", align: "right", render: (v: number) => <Text strong >${Number(v ?? 0).toFixed(2)}</Text> },
	];

	return (
		<>
			<DataTable<SalesOrder>
				columns={columns}
				dataSource={orders}
				rowKey={(r) => r.sales_order_id ?? Math.random()}
				loading={loading}
				onRowClick={(r) => setSelectedOrder(r)}
				pagination={{ current: page, pageSize: perPage, total, showSizeChanger: false, showTotal: (t) => `${t} orders`, onChange: (p) => setPage(p) }}
				emptyTitle={emptyTitle}
			/>
			<Modal
				open={!!selectedOrder}
				onCancel={() => setSelectedOrder(null)}
				destroyOnClose
				width={800}
				bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
				title={selectedOrder ? (selectedOrder.shopify_order_identifier ?? `#${selectedOrder.sales_order_id}`) : ""}
				footer={[
					<Button key="close" type="primary" onClick={() => setSelectedOrder(null)}>
						Close
					</Button>,
				]}
			>
				{selectedOrder && <OrderDetail order={selectedOrder} />}
			</Modal>
		</>
	);
};

export default OrdersTab;